import * as React from "react";
import { useIcon } from "@salt-ds/core";

import { Button } from "../controls/Button";
import { useControlled } from "../forms/useControlled";
import "./pagination.css";

/**
 * Pagination, Paginator and PageButton, replacing `@salt-ds/core`'s behind
 * `table-pagination.tsx`.
 *
 * ── IT ANNOUNCES THE PAGE CHANGE, BECAUSE NOTHING ELSE WILL ───────────────────────────
 *
 * Clicking "3" replaces the table below with entirely different rows, and a screen reader has no
 * way to know that: focus stays on the button, and the region that changed is somewhere else on
 * the page. So the change is announced explicitly, and NOT on first render — announcing "Page 1"
 * when the page loads describes something that did not happen.
 *
 * ── A `nav` THAT DISAPPEARS BELOW TWO PAGES ───────────────────────────────────────────
 *
 * `count < 2` renders nothing at all. Pagination for a single page is a control that cannot do
 * anything, and as a `nav` landmark it would also add a permanent, pointless stop to the
 * document's landmark list.
 */
interface PaginationContextValue {
  page: number;
  count: number;
  onPageChange: (event: React.SyntheticEvent, page: number) => void;
}

const PaginationContext = React.createContext<PaginationContextValue | null>(null);

function usePaginationContext(): PaginationContextValue {
  const context = React.useContext(PaginationContext);
  if (!context) {
    throw new Error("Pagination parts must be rendered inside a <Pagination>.");
  }
  return context;
}

export interface PaginationProps extends React.HTMLAttributes<HTMLElement> {
  count: number;
  page?: number;
  defaultPage?: number;
  onPageChange?: (event: React.SyntheticEvent, page: number) => void;
}

export const Pagination = React.forwardRef<HTMLElement, PaginationProps>(function Pagination(
  { children, className, count, defaultPage = 1, onPageChange: onPageChangeProp, page: pageProp, ...rest },
  ref,
) {
  const [page, setPage] = useControlled<number>({
    controlled: pageProp,
    default: defaultPage,
  });

  const onPageChange = (event: React.SyntheticEvent, next: number) => {
    setPage(next);
    onPageChangeProp?.(event, next);
  };

  /*
   * Announced through a visually-hidden live region, and NOT on the first render.
   *
   * `mounted` guards that: without it the component announces "Page 1" the moment it appears,
   * describing a change that never happened — which is noise on every page load, and trains a
   * user to ignore the region that matters.
   */
  const [announcement, setAnnouncement] = React.useState("");
  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (mounted.current) setAnnouncement(`Page ${page}`);
    else mounted.current = true;
  }, [page]);

  // Not memoised: `onPageChange` closes over the caller's handler, which closes over page state.
  const contextValue: PaginationContextValue = { page, count, onPageChange };

  // Nothing to paginate. See the note at the top — this also keeps a useless landmark out of
  // the document.
  if (count < 2) return null;

  return (
    <PaginationContext.Provider value={contextValue}>
      <nav
        className={className ? `saltPagination ${className}` : "saltPagination"}
        ref={ref}
        {...rest}
      >
        {children}
        <span aria-live="polite" className="saltPagination-announcer" role="status">
          {announcement}
        </span>
      </nav>
    </PaginationContext.Provider>
  );
});

export interface PageButtonProps {
  page: number;
  selected?: boolean;
  disabled?: boolean;
}

export const PageButton = React.forwardRef<HTMLButtonElement, PageButtonProps>(
  function PageButton({ disabled, page, selected }, ref) {
    const { count, onPageChange } = usePaginationContext();

    return (
      <button
        // `aria-current="page"` is what tells a screen reader WHICH page is showing. Colour
        // alone conveys that to nobody who cannot see it.
        aria-current={selected ? "page" : undefined}
        // "Page 3 of 12", not "3" — a bare number in a row of numbers means nothing read aloud.
        aria-label={`Page ${page} of ${count}`}
        /*
         * NO `saltPageButton-selected` class. The vendor emits one and nothing defines it —
         * the selected page is styled by `.saltPageButton[aria-current=page]`, so the ARIA
         * attribute drives the visual state directly.
         *
         * That is the right design and worth keeping deliberately: the thing a screen reader
         * announces and the thing a sighted user sees are the SAME source, so they cannot
         * drift apart. A separate class could be applied without the attribute, leaving a page
         * that looks current and does not announce as current.
         */
        className={[
          "saltPageButton",
          // Below 100 every button is the same fixed width, so the row does not reflow as the
          // digits change. Past that they size to content rather than truncating.
          page < 100 ? "saltPageButton-fixed" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={disabled}
        onClick={(event) => onPageChange(event, page)}
        ref={ref}
        type="button"
      >
        {page}
      </button>
    );
  },
);

/**
 * Which page numbers to show, and where the ellipses fall.
 *
 * `2 * (boundaryCount + siblingCount) + 3` is the widest the control ever gets: both boundaries,
 * both sibling runs, the current page, and the two ellipses. If the total page count fits inside
 * that, every page is listed and no ellipsis appears — which is why a 5-page table shows
 * 1 2 3 4 5 rather than 1 … 3 … 5.
 */
function paginationRanges(
  page: number,
  count: number,
  boundaryCount: number,
  siblingCount: number,
): [range | undefined, range | undefined, range | undefined] {
  const totalButtonsVisible = 2 * (boundaryCount + siblingCount) + 3;
  const isEnoughSpace = totalButtonsVisible >= count;
  const isLeftEllipsis = !isEnoughSpace && page - boundaryCount - siblingCount > 2;
  const isRightEllipsis = !isEnoughSpace && page + boundaryCount + siblingCount + 1 < count;

  const leftPages: range | undefined = isLeftEllipsis ? [1, boundaryCount] : undefined;
  const rightPages: range | undefined = isRightEllipsis
    ? [count - boundaryCount + 1, count]
    : undefined;

  let middlePages: range;
  if (!isLeftEllipsis) {
    // Near the start: run from 1 forward, so the first pages stay reachable in one click.
    middlePages = [1, isRightEllipsis ? boundaryCount + 2 + 2 * siblingCount : count];
  } else if (!isRightEllipsis) {
    // Near the end: mirror image of the above.
    middlePages = [count - boundaryCount - 1 - 2 * siblingCount, count];
  } else {
    // In the middle: centre the window on the current page.
    middlePages = [page - siblingCount, page + siblingCount];
  }

  return [leftPages, middlePages, rightPages];
}

type range = [number, number];

function mapRange<T>(r: range, fn: (i: number) => T): T[] {
  const result: T[] = [];
  for (let i = r[0]; i <= r[1]; i += 1) result.push(fn(i));
  return result;
}

function Ellipsis() {
  // A div, not a button: it is a gap indicator, and making it focusable would put a tab stop on
  // something that cannot be activated.
  return <div className="saltPageRanges-ellipsis">…</div>;
}

export interface PaginatorProps extends React.HTMLAttributes<HTMLDivElement> {
  boundaryCount?: number;
  siblingCount?: number;
}

export const Paginator = React.forwardRef<HTMLDivElement, PaginatorProps>(function Paginator(
  { boundaryCount = 1, className, siblingCount = 2, ...rest },
  ref,
) {
  const { count, onPageChange, page } = usePaginationContext();
  const { NextIcon, PreviousIcon } = useIcon();

  const [leftPages, middlePages, rightPages] = paginationRanges(
    page,
    count,
    // At least 1: a boundary of 0 would remove the first and last pages entirely, leaving no way
    // back to page 1 from the middle of a long table.
    Math.max(1, boundaryCount),
    siblingCount,
  );

  const renderPages = (r: range) =>
    mapRange(r, (i) => <PageButton key={i} page={i} selected={page === i} />);

  return (
    <div
      className={className ? `saltPaginator ${className}` : "saltPaginator"}
      ref={ref}
      {...rest}
    >
      <Button
        appearance="transparent"
        aria-label="Previous Page"
        className="saltPaginator-arrowButton-previous"
        // Disabled rather than hidden at the ends: a control that vanishes shifts everything
        // beside it, and the row of page numbers would jump as the user pages through.
        disabled={page === 1}
        onClick={(event) => onPageChange(event, Math.max(1, page - 1))}
      >
        <PreviousIcon aria-hidden />
      </Button>
      {leftPages ? (
        <>
          {renderPages(leftPages)}
          <Ellipsis />
        </>
      ) : null}
      {middlePages ? renderPages(middlePages) : null}
      {rightPages ? (
        <>
          <Ellipsis />
          {renderPages(rightPages)}
        </>
      ) : null}
      <Button
        appearance="transparent"
        aria-label="Next Page"
        className="saltPaginator-arrowButton-next"
        disabled={page === count}
        onClick={(event) => onPageChange(event, Math.min(page + 1, count))}
      >
        <NextIcon aria-hidden />
      </Button>
    </div>
  );
});
